/**
 * HI_LO (cee-lo) — simultaneous single-die rolls to pick the banker.
 *
 * Each contender rolls one die. On a tie, the tied contenders re-roll
 * (server cycles through hiLoRounds). The local contender's onEnter
 * gates the player:ready ACK on the dice-authorize signal, same
 * pattern as the regular roll states.
 *
 * The dice-result flyout for HI_LO contenders rides the transient
 * `lastRollResult` channel — server doesn't surface single-die
 * outcomes via `player.result`. We DO NOT call _checkPlayers here:
 * its `null target → animate-out existing icon` diff would clobber
 * each previous contender's flyout-placed icon as soon as the next
 * contender's update arrives. The icons are placed exclusively via
 * handleNewRollResult and live in the overlay's flyout cache for
 * the duration of the HI_LO phase.
 *
 * onExit fires the HI_LO resolution sequence (bank flyout to winner,
 * gear-out for losers, reveal animation, dice cleanup).
 */

import type { GameUpdate } from '@sdl/shared';
import { State } from '../State.js';
import { HiLoAnims } from '../../../config/AnimationConfig';

export class HiLoState extends State {
  private lastSeenRoundsLength = 0;

  async onEnter(update: GameUpdate): Promise<void> {
    const game = this.game;

    this.lastSeenRoundsLength = update.hiLoRounds?.length ?? 0;
    this._resetRollSetup();

    game.playerProfilesOverlay?.show();
    game.playerProfilesOverlay?.setMode('hi_lo');
    game.bettingHUD3D?.setLockInVisible(false);
    game.countdownOverlay?.stop();

    // Pin every opponent's activeDiceCount to 1 — see CrapsHiLoState
    // for the full reasoning. Cee-lo equips 3, but HI_LO rolls 1.
    const opp = game.opponentDiceManager;
    if (opp) {
      for (const id of update.players?.map(p => p.playerId) ?? []) {
        opp.setActiveDiceCount(id, 1);
      }
    }

    await game.exitBettingMode({
      animated: update.animated,
      previousState: update.previousState,
    });

    this.applyInstruction(update);
    this._updateProfiles(update);

    // Authorization for HI_LO rides the controller's follow-up broadcast (it
    // runs post-transition), so this usually fires from onUpdate. The keyed
    // guard re-spawns on a tie re-roll (hiLoRounds grows) but never twice for
    // the same round.
    this._maybeSetupLocalRoll(update);
  }

  async onUpdate(update: GameUpdate): Promise<void> {
    const game = this.game;

    // Tie → re-roll: when hiLoRounds grows, drop the dice-result dedup
    // so a re-roll landing on the same value still flyouts.
    const rounds = update.hiLoRounds?.length ?? 0;
    const newRound = rounds > this.lastSeenRoundsLength;
    if (newRound) {
      game.clearResultDedup();
      this.lastSeenRoundsLength = rounds;
      // A new HI_LO round (tie re-roll) is the server-authoritative signal
      // that the surviving contenders must throw again. Re-arm the roll-setup
      // gate from STATE here instead of trusting the authorized→unauthorized
      // edge in _maybeSetupLocalRoll — that edge can be missed across the
      // back-to-back deauth/reauth broadcasts, leaving the tied player unable
      // to re-roll (the tie hang). With the gate reset, the authorized check
      // below re-spawns the die for any contender still in rollAuthorizedFor.
      this._resetRollSetup();
    }

    if (update.lastRollResult) {
      game.handleNewRollResult(update.lastRollResult);
    }

    this._updateProfiles(update);
    this.applyInstruction(update);

    this._maybeSetupLocalRoll(update);
  }

  async onExit(update: GameUpdate): Promise<void> {
    // HI_LO resolution chain — explicit subsystem calls, no legacy
    // god-method. Single source of truth for profile data is the
    // overlay's cache via _updateProfiles. Drop the historical
    // hideBankerBadge call (it clobbered the badge that flyoutBankIcon
    // had just placed on the winner) and the legacy updateProfiles
    // call (which read the controller's stale playerEquippedDice cache,
    // remove-recreating gear containers and breaking the reveal
    // animation).
    const game = this.game;
    const overlay = game.playerProfilesOverlay;
    if (!overlay) return;
    if (!update.animated) return;

    await overlay.waitForAllFlyouts();

    // bankerId is wired to the HI_LO winner by both server strategies
    // (cee-lo's banker, craps's shooter). Shared field, no game branch.
    const winnerId = update.bankerId;
    if (winnerId) {
      const screenPos = game.getDiceCenterScreen(winnerId);
      if (screenPos) {
        await overlay.flyoutBankIcon(
          winnerId,
          screenPos,
          HiLoAnims.winnerRevealDuration + 0.3,
        );
      }
    }

    // Animate HI_LO dice result icons out from every profile card.
    // Fire-and-forget — overlaps with the delay + gear-out below.
    overlay.clearAllDiceResults(true);
    // Drop the dedup set so a same-value re-roll next round still
    // triggers a new flyout.
    game.clearResultDedup();

    await game.delay(300);

    // Gear-out for losers only — winner's gear stays at scale 1 the
    // whole time, so there's no disappear-then-reappear flicker.
    // (Previously we scaled everyone out then revealWinner animated
    // the winner's gear back in; that path required a fragile
    // "preserve at scale 0 across a setPlayers refresh" dance which
    // was breaking when caches diverged.)
    await overlay.animateDiceGearOut(HiLoAnims.diceGearOutDuration, winnerId);

    // Refresh profiles via the overlay's cache. Post-HI_LO showDice
    // flips to "winner only", so losers' gear containers are removed;
    // winner's gear container stays (sig matches). The bank badge
    // placed by flyoutBankIcon also stays — updateBankBadge sees it
    // on the winner's circle and isBanker=true, no-op.
    this._updateProfiles(update);

    await game.delay(500);

    // Pre-spawn the first round's banker zone before BANKER_SET_POT
    // starts its camera lerp. Same idea as CrapsHiLoState.onExit; gets
    // the zone into the 3D scene so the camera passes over a visible
    // zone instead of an empty patch of floor mid-lerp.
    game.primeBetZones();

    // Dice cleanup is owned by the destination state's
    // enterBettingMode (BANKER_SET_POT) — fades dice off the table
    // in lock-step with the camera lerp instead of in a separate
    // beat here.
  }

  private applyInstruction(update: GameUpdate): void {
    const localId = this.game.getLocalPlayerId();
    const contenders = update.hiLoContenders ?? [];
    const rolls = update.hiLoRolls ?? {};
    const isContender = !!localId && contenders.includes(localId);
    const hasRolled = !!localId && Object.prototype.hasOwnProperty.call(rolls, localId);
    const isReroll = (update.hiLoRounds?.length ?? 0) > 0;
    const banner = this.game.instructionBanner;
    if (!banner) return;
    if (isContender && !hasRolled) {
      banner.show({
        title: 'Hi-Lo',
        text: isReroll ? 'Re-roll to break the tie' : 'Roll to determine the banker',
      }, update.animated);
    } else {
      banner.show({
        title: 'Hi-Lo',
        text: isReroll ? 'Players are re-rolling to break the tie…' : 'Players are rolling for banker…',
      }, update.animated);
    }
  }
}
